import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { onAuthStateChanged, onIdTokenChanged } from 'firebase/auth';
import { CircularProgress, Box } from '@mui/material';
import { useAppSelector, useAppDispatch } from '../hooks/redux';
import { setUser, initializeStoredToken, logoutUser, setToken, setMongoVerified, setTermsStatus } from '../store/authSlice';
import { auth } from '../config/firebase';
import { validateAndRefreshToken, storeToken } from '../utils/tokenUtils';
import { fetchAuthMe } from '../services/userService';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const dispatch = useAppDispatch();
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  const [loading, setLoading] = useState(true);
  const [tokenValidated, setTokenValidated] = useState(false);

  // Setup auth state listener - only once on mount
  useEffect(() => {
  
    // Initialize stored token first (only once)
    dispatch(initializeStoredToken());
    
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      
      if (firebaseUser) {
        // User is logged in with Firebase, validate JWT token
        console.log('[ProtectedRoute] Firebase user detected, validating JWT token...');
        
        try {
          const { isValid, token } = await validateAndRefreshToken();
          
          if (!isValid || !token) {
            console.warn('[ProtectedRoute] ❌ JWT token invalid, logging out user');
            // Token is invalid, log the user out
            await dispatch(logoutUser());
            setTokenValidated(false);
            setLoading(false);
            return;
          }
          
          // Token is valid, update state
          console.log('[ProtectedRoute] ✅ JWT token validated successfully');
          dispatch(setUser(firebaseUser));
          
          // Get fresh token info
          const tokenResult = await firebaseUser.getIdTokenResult();
          dispatch(setToken({
            token: token,
            expiresAt: new Date(tokenResult.expirationTime).getTime(),
            issuedAt: new Date(tokenResult.issuedAtTime).getTime()
          }));
          
          // Get user profile to check role and MongoDB existence
          try {
            const authMe = await fetchAuthMe();
            const userProfile = authMe?.user;

            if (!userProfile) {
              // User doesn't exist in MongoDB
              console.warn('[ProtectedRoute] User not found in database');
              dispatch(setMongoVerified(false));
              setTokenValidated(false);
              await dispatch(logoutUser());
              return;
            }

            // User exists in MongoDB
            console.log('[ProtectedRoute] User verified:', userProfile?.email, 'Role:', userProfile?.role);
            dispatch(setMongoVerified(true));
            // Terms status may be absent on older backends — null means "no gate".
            dispatch(setTermsStatus(authMe?.termsStatus ?? null));
            setTokenValidated(true);

            // Store user role info if needed
            // You can dispatch an action here to store the user role in Redux if needed
            // dispatch(setUserRole(userProfile.role));

          } catch (error) {
            console.error('[ProtectedRoute] Error verifying user:', error);
            dispatch(setMongoVerified(false));
            setTokenValidated(false);
            await dispatch(logoutUser());
          }
        } catch (error) {
          console.error('[ProtectedRoute] Error during token validation:', error);
          await dispatch(logoutUser());
          setTokenValidated(false);
        }
      } else {
        // No user, clear state
        console.log('[ProtectedRoute] No Firebase user, clearing state');
        dispatch(setUser(null));
        await dispatch(logoutUser());
        setTokenValidated(false);
      }
      
      setLoading(false);
    });


    return () => {
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]); // Only depend on dispatch to avoid re-registering listener

  // Keep Redux + localStorage in sync with Firebase's silent token refresh.
  // Firebase fires onIdTokenChanged on every refresh (~every 55 minutes) so we
  // never need to poll. Without this, Redux holds the original sign-in token
  // forever and any WS reconnect after expiry uses a dead token.
  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        return;
      }

      try {
        const tokenResult = await firebaseUser.getIdTokenResult();
        const tokenInfo = {
          token: tokenResult.token,
          expiresAt: new Date(tokenResult.expirationTime).getTime(),
          issuedAt: new Date(tokenResult.issuedAtTime).getTime(),
        };

        dispatch(setToken(tokenInfo));
        storeToken(tokenInfo);
        console.debug('[ProtectedRoute] 🔄 ID token refreshed, Redux + storage synced');
      } catch (error) {
        console.error('[ProtectedRoute] Failed to sync refreshed ID token:', error);
      }
    });

    return () => {
      unsubscribe();
    };
  }, [dispatch]);


  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        className="bg-surface-subtle"
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (isAuthenticated && tokenValidated) {
    return <>{children}</>;
  } else {
    console.log('[ProtectedRoute] ❌ Redirecting to login');
    return <Navigate to="/login" replace />;
  }
};

export default ProtectedRoute;